/**
 * Scenery backdrop: a calm small-town edge — rolling hills + forest, a few
 * buildings and a couple of shops, distant pedestrians, and a paved street in
 * front. Day/night is a single cross-fade (`setDay`): the scene starts at night
 * (moon + stars) and the house click raises the sun.
 *
 * Only the clouds move here; everything else is static (the user found the
 * blinking windows distracting — they're now lit on a fixed seed). The
 * celestial bodies sit at the back of the sky, behind all scenery, so the
 * world's night-tint overlay (added on top by `world.ts`) reads correctly.
 *
 * Composition, back → front:
 *   sky (night⇄day) · stars · moon · sun · clouds · hills · forest · buildings
 *   · shops · distant people · pavement · hedge · lampposts · vignette
 */
import { Container, Graphics } from 'pixi.js'
import { PALETTE as P, pick } from './palette'
import { VIRTUAL_W as W, VIRTUAL_H as H, HORIZON_Y } from '../sceneLayout'
import { rect, bands, circle, poly } from './draw'
import { drawDash8 } from './objects'

export interface Backdrop {
  container: Container
  /** day = 1 (sun, bright sky), night = 0 (moon, stars, dark sky). */
  setDay(progress: number): void
  /** Fade the parked Q400 in/out (travel-insured). */
  setPlaneVisible(visible: boolean): void
  tick(dtMs: number): void
}

/** Deterministic LCG so the scenery is identical on every reload + in tests. */
function rng(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 0xffffffff
  }
}

export function buildBackdrop(): Backdrop {
  const container = new Container()
  container.label = 'backdrop'
  const rand = rng(0x5ab)

  // ─── Sky (night + day cross-fade) ──────────────────────────────────────────
  const nightSky = new Graphics()
  bands(nightSky, 0, 0, W, HORIZON_Y + 4, P.nightSky)
  container.addChild(nightSky)

  const daySky = new Graphics()
  bands(daySky, 0, 0, W, HORIZON_Y + 4, P.sky)
  daySky.alpha = 0
  container.addChild(daySky)

  // Stars (night only) — a dense field with a few brighter twinkles
  const stars = new Graphics()
  for (let i = 0; i < 96; i++) {
    const x = Math.floor(rand() * W)
    const y = Math.floor(rand() * 112)
    const b = rand()
    rect(stars, x, y, 1, 1, P.star, 0.35 + 0.55 * b)
    if (b > 0.9) {
      rect(stars, x - 1, y, 3, 1, P.star, 0.85) // twinkle cross
      rect(stars, x, y - 1, 1, 3, P.star, 0.7)
    }
  }
  container.addChild(stars)

  // Moon (sets to the left as day arrives) with a big soft halo
  const moonC = new Container()
  const mg = new Graphics()
  circle(mg, 0, 0, 26, P.moon, 0.05)
  circle(mg, 0, 0, 19, P.moon, 0.1)
  circle(mg, 0, 0, 13, P.moon, 0.2)
  circle(mg, 0, 0, 10, P.moon)
  circle(mg, 3, -2, 2, P.moonCrater, 0.55)
  circle(mg, -3, 3, 1.6, P.moonCrater, 0.5)
  circle(mg, 2, 4, 1, P.moonShadow, 0.5)
  circle(mg, -4, -3, 1, P.moonCrater, 0.4)
  moonC.addChild(mg)
  moonC.position.set(148, 46)
  container.addChild(moonC)

  // Sun (rises on the right as day arrives)
  const sunC = new Container()
  const sg = new Graphics()
  circle(sg, 0, 0, 26, 0xfff0c8, 0.1)
  circle(sg, 0, 0, 18, 0xfff0c8, 0.16)
  circle(sg, 0, 0, 11, 0xfff6da, 0.95)
  sunC.addChild(sg)
  sunC.position.set(386, 150)
  sunC.alpha = 0
  container.addChild(sunC)

  // ─── Clouds (drift; fainter at night) ──────────────────────────────────────
  const clouds = new Container()
  clouds.label = 'clouds'
  const cloudData: { g: Graphics; x: number; speed: number }[] = []
  for (let i = 0; i < 5; i++) {
    const g = new Graphics()
    const cw = 36 + Math.floor(rand() * 30)
    // shadowed underside
    rect(g, 2, 11, cw - 4, 5, P.cloudShadow)
    rect(g, 8, 14, cw - 16, 3, P.cloudShadow)
    // body + soft rounded puffs (lighter on top)
    rect(g, 2, 6, cw - 4, 7, P.cloud)
    circle(g, 12, 7, 6, P.cloud)
    circle(g, cw / 2 - 2, 4, 7, P.cloud)
    circle(g, cw - 14, 6, 6, P.cloud)
    circle(g, cw / 2 + 10, 6, 5, P.cloud)
    // crisp highlight along the top
    rect(g, 8, 3, cw - 22, 2, 0xf6f9fb, 0.6)
    const cx = Math.floor(rand() * W)
    g.x = cx
    g.y = 8 + Math.floor(rand() * 42)
    clouds.addChild(g)
    cloudData.push({ g, x: cx, speed: 1.1 + rand() * 1.6 })
  }
  container.addChild(clouds)

  // ─── Rolling hills (3 layers, far = hazier) ────────────────────────────────
  const hills = new Graphics()
  const hill = (topMin: number, amp: number, freq: number, phase: number, color: number) => {
    const pts: number[] = []
    for (let x = 0; x <= W; x += 6) {
      const y = Math.round(topMin + amp * (0.5 + 0.5 * Math.sin(x * freq + phase)))
      pts.push(x, y)
    }
    pts.push(W, HORIZON_Y + 4, 0, HORIZON_Y + 4)
    poly(hills, pts, color)
  }
  hill(116, 18, 0.018, 0.6, P.hillBack)
  hill(128, 20, 0.022, 2.4, P.hillMid)
  hill(142, 14, 0.03, 4.1, P.hillFront)
  rect(hills, 0, HORIZON_Y - 2, W, 4, P.hillFrontDark, 0.4) // base shadow
  container.addChild(hills)

  // ─── Forest: tree clusters on the hill, mostly at the sides ───────────────
  const forest = new Graphics()
  const conifer = (x: number, baseY: number, h: number) => {
    rect(forest, x - 1, baseY, 2, 3, P.treeTrunk)
    for (let i = 0; i < 3; i++) {
      const ty = baseY - 3 - i * (h / 3)
      const tw = (h / 2) * (1 - i * 0.25)
      poly(forest, [x - tw, ty, x + tw, ty, x, ty - h / 3 - 2], i === 0 ? P.treeConifer : P.treeConiferDark)
    }
    poly(forest, [x - h / 2, baseY - 3, x + h / 2, baseY - 3, x, baseY - 3 - h / 3 - 2], P.treeConifer)
  }
  const decid = (x: number, baseY: number, r: number) => {
    rect(forest, x - 1, baseY - r, 2, r + 2, P.treeTrunk)
    circle(forest, x, baseY - r - 2, r, P.treeDecidDark)
    circle(forest, x - 1, baseY - r - 3, r - 1, P.treeDecid)
  }
  for (let x = 4; x < 116; x += 9) {
    const b = HORIZON_Y - 1 + Math.floor(rand() * 3)
    if (rand() > 0.5) conifer(x, b, 9 + Math.floor(rand() * 4))
    else decid(x, b, 4 + Math.floor(rand() * 2))
  }
  for (let x = 392; x < W; x += 9) {
    const b = HORIZON_Y - 1 + Math.floor(rand() * 3)
    if (rand() > 0.5) conifer(x, b, 9 + Math.floor(rand() * 4))
    else decid(x, b, 4 + Math.floor(rand() * 2))
  }
  // a couple of lone trees behind the centre for depth
  decid(300, HORIZON_Y - 1, 4)
  conifer(338, HORIZON_Y - 1, 10)
  container.addChild(forest)

  // ─── A few buildings (two small clusters, much sparser than before) ────────
  const blds = new Graphics()
  // Taller, denser skyline of lit apartment blocks (left + right of the house).
  const buildingSpots: { x: number; w: number; h: number }[] = [
    { x: 100, w: 28, h: 58 },
    { x: 132, w: 24, h: 42 },
    { x: 162, w: 30, h: 66 },
    { x: 198, w: 26, h: 48 },
    { x: 376, w: 28, h: 60 },
    { x: 408, w: 24, h: 44 },
    { x: 446, w: 28, h: 64 },
  ]
  buildingSpots.forEach((b, bi) => {
    const top = HORIZON_Y - b.h
    rect(blds, b.x, top, b.w, b.h, pick(P.midBuilding, bi))
    rect(blds, b.x + b.w - 5, top, 5, b.h, pick(P.midBuildingShadow, bi))
    rect(blds, b.x, top, b.w, 2, pick(P.midBuildingShadow, bi))
    for (let wy = top + 5; wy < HORIZON_Y - 4; wy += 6) {
      for (let wx = b.x + 3; wx < b.x + b.w - 4; wx += 5) {
        const lit = rand() > 0.42
        rect(blds, wx, wy, 3, 4, lit ? P.windowLit : P.windowDark)
        if (lit && rand() > 0.5) rect(blds, wx, wy + 4, 3, 1, P.windowLit, 0.4) // warm spill
      }
    }
  })
  container.addChild(blds)

  // ─── Shops: a Biedronka-style storefront + a small grocer ──────────────────
  const shops = new Graphics()
  shopFront(shops, 96, 28, P.awningBlue, P.awningBlueDark, false) // grocer (left)
  shopFront(shops, 150, 28, P.awningRed, P.awningRedDark, true) // Biedronka
  container.addChild(shops)

  // ─── Distant people on the horizon (left/centre, clear of the airfield) ────
  const people = new Graphics()
  const peopleSpots = [120, 196, 232, 246, 318]
  peopleSpots.forEach((x, i) => {
    const c = pick(P.crowd, i)
    rect(people, x, 153, 2, 2, P.crowdSkin) // head
    rect(people, x, 155, 2, 4, c) // body
    rect(people, x, 158, 1, 1, 0x2b2b2b) // foot shadow gap
  })
  container.addChild(people)

  // ─── A small Q400 cruising across the sky (travel-insured only) ───────────
  // Drifts slowly left → right at altitude, wrapping around. The world fades
  // it in when a travel contract exists; otherwise it's invisible.
  const planeC = new Container()
  planeC.label = 'sky-plane'
  let planeX = 80 // local plane position, drifts each frame
  planeC.position.set(planeX, 38)
  planeC.scale.set(0.42) // small — clearly a distant airliner
  planeC.alpha = 0
  const pg = new Graphics()
  drawDash8(pg, 0, 0, 1, { gear: false, noseRight: true })
  planeC.addChild(pg)
  container.addChild(planeC)

  // ─── Ground: paved street, depth bands + perspective seams ─────────────────
  const ground = new Graphics()
  rect(ground, 0, HORIZON_Y, W, 28, P.pavementFar)
  rect(ground, 0, HORIZON_Y + 28, W, 34, P.pavement)
  rect(ground, 0, HORIZON_Y + 62, W, H - (HORIZON_Y + 62), P.pavementNear)
  rect(ground, 0, HORIZON_Y + 27, W, 1, P.pavementSeam, 0.6)
  rect(ground, 0, HORIZON_Y + 61, W, 1, P.pavementSeam, 0.6)
  const vanishX = W / 2
  for (let i = -6; i <= 6; i++) {
    const baseX = vanishX + i * 40
    poly(ground, [vanishX, HORIZON_Y, baseX - 1, H, baseX + 1, H], P.pavementSeam, 0.12)
  }
  rect(ground, 0, HORIZON_Y + 1, W, 2, P.curb, 0.7)
  container.addChild(ground)

  // ─── Roadside hedge ─────────────────────────────────────────────────────────
  const hedge = new Graphics()
  const hy = HORIZON_Y + 6
  rect(hedge, 0, hy, W, 8, P.hedge)
  rect(hedge, 0, hy, W, 2, 0x5f7d48)
  rect(hedge, 0, hy + 7, W, 2, P.hedgeDark)
  for (let x = 0; x < W; x += 3) {
    if (rand() > 0.6) rect(hedge, x, hy + 1, 1, 2, P.hedgeDark, 0.6)
  }
  container.addChild(hedge)

  // ─── The car's garage: a foreground building the car is parked in front of ─
  const garage = new Graphics()
  foregroundGarage(garage, 114, 72, 214)
  container.addChild(garage)

  // ─── Street lamps: modest posts, shorter than the buildings ────────────────
  const lampSpots = [44, 96, 370, 456]
  const props = new Graphics()
  for (const lx of lampSpots) {
    rect(props, lx - 2, HORIZON_Y - 1, 5, 2, P.lampPost) // base
    rect(props, lx - 1, HORIZON_Y - 34, 2, 34, P.lampPost) // pole
    rect(props, lx - 2, HORIZON_Y - 38, 5, 4, P.lampPost) // housing
    rect(props, lx - 3, HORIZON_Y - 39, 7, 1, P.lampPost) // cap
    rect(props, lx - 1, HORIZON_Y - 37, 3, 3, P.lampGlow) // lit pane
  }
  container.addChild(props)

  // ─── Night lights: a warm glow around each lamp head + a faint wet sheen ───
  const nightLights = new Graphics()
  rect(nightLights, 0, HORIZON_Y, W, 30, P.wetSheen, 0.08) // faint sheen near the kerb
  rect(nightLights, 0, HORIZON_Y, W, 12, P.wetSheen, 0.08)
  for (const lx of lampSpots) {
    circle(nightLights, lx, HORIZON_Y - 35, 9, P.lampGlow, 0.14) // soft glow around the head
    circle(nightLights, lx, HORIZON_Y - 35, 5, P.lampGlow, 0.28)
  }
  // pale moon reflection shimmering on the wet street
  for (let s = 0; s < 6; s++) {
    rect(nightLights, 147, HORIZON_Y + 2 + s * 9, 2, 6, P.moon, 0.1 * (1 - s / 6))
  }
  container.addChild(nightLights)

  // ─── Front vignette for depth ──────────────────────────────────────────────
  const vignette = new Graphics()
  rect(vignette, 0, H - 14, W, 14, P.shadow, 0.12)
  rect(vignette, 0, H - 6, W, 6, P.shadow, 0.16)
  container.addChild(vignette)

  // ─── API ────────────────────────────────────────────────────────────────────
  let planeTarget = 0
  return {
    container,
    setDay(p) {
      nightSky.alpha = 1 - p
      daySky.alpha = p
      stars.alpha = 1 - p
      moonC.alpha = 1 - p
      moonC.y = 46 + p * 28
      sunC.alpha = p
      sunC.y = 150 - p * 104
      clouds.alpha = 0.4 + 0.5 * p
      nightLights.alpha = 1 - p // lamp glow + wet reflections are night-only
    },
    setPlaneVisible(visible) {
      planeTarget = visible ? 1 : 0
    },
    tick(dtMs: number) {
      for (const c of cloudData) {
        c.x += (c.speed * dtMs) / 1000
        if (c.x > W + 4) c.x = -50
        c.g.x = Math.round(c.x)
      }
      planeC.alpha += (planeTarget - planeC.alpha) * Math.min(1, dtMs / 260)
      // Slow eastbound drift; wrap around when fully off the right edge.
      planeX += (dtMs * 18) / 1000
      if (planeX > W + 40) planeX = -60
      planeC.x = Math.round(planeX)
    },
  }
}

/** A foreground residential garage (house-matched) — the car parks in front of it. */
function foregroundGarage(g: Graphics, x: number, w: number, base: number): void {
  const eave = base - 42
  const peak = eave - 16
  // walls
  rect(g, x, eave, w, base - eave, P.wall)
  rect(g, x, eave, 4, base - eave, P.wallLight)
  rect(g, x + w - 5, eave, 5, base - eave, P.wallShadow)
  rect(g, x, base - 3, w, 3, P.foundationDark) // base lip
  // gable roof with a little overhang
  poly(g, [x - 3, eave + 1, x + w + 3, eave + 1, x + w / 2, peak], P.roof)
  poly(g, [x - 3, eave + 1, x + w / 2, eave + 1, x + w / 2, peak], P.roofLight)
  rect(g, x - 3, eave, w + 6, 2, P.roofShadow)
  // up-and-over sectional door
  const dx = x + 7
  const dw = w - 14
  const dy = eave + 7
  const dBottom = base - 3
  rect(g, dx, dy, dw, dBottom - dy, P.doorWood)
  rect(g, dx, dy, dw, 1, P.doorWoodDark)
  rect(g, dx, dy, 1, dBottom - dy, P.doorWoodDark, 0.6)
  rect(g, dx + dw - 1, dy, 1, dBottom - dy, P.doorWoodDark, 0.6)
  for (let py = dy + 5; py < dBottom; py += 5) rect(g, dx, py, dw, 1, P.doorWoodDark, 0.5)
}

/** A small storefront with an awning + signboard; `biedronka` adds a ladybug. */
function shopFront(
  g: Graphics,
  x: number,
  w: number,
  awning: number,
  awningDark: number,
  biedronka: boolean,
): void {
  const top = HORIZON_Y - 28
  // wall
  rect(g, x, top + 6, w, 22, P.shopWall)
  rect(g, x + w - 4, top + 6, 4, 22, P.shopWallShadow)
  // window + door
  rect(g, x + 3, top + 14, w - 14, 10, P.shopWindow)
  rect(g, x + 3, top + 14, w - 14, 3, 0xb6cdd8)
  rect(g, x + w - 9, top + 12, 6, 16, P.shopDoor)
  // signboard
  rect(g, x, top, w, 6, P.signBoard)
  rect(g, x, top, w, 1, 0xd8d0bf)
  // striped awning
  for (let i = 0; i < w; i += 4) {
    rect(g, x + i, top + 6, 2, 3, awning)
    rect(g, x + i + 2, top + 6, 2, 3, P.awningCream)
  }
  rect(g, x, top + 9, w, 1, awningDark)
  if (biedronka) {
    // ladybug logo centred on the sign
    const lx = x + Math.round(w / 2)
    circle(g, lx, top + 3, 2.4, P.ladybug)
    rect(g, lx - 0.5, top + 1, 1, 5, P.ladybugSpot, 0.7) // wing split
    rect(g, lx - 2, top + 1, 2, 1, P.ladybugSpot) // head
    rect(g, lx - 1, top + 3, 1, 1, P.ladybugSpot)
    rect(g, lx + 1, top + 4, 1, 1, P.ladybugSpot)
  } else {
    // simple grocer mark: a green leaf
    const lx = x + Math.round(w / 2)
    circle(g, lx, top + 3, 2, P.treeDecid)
    rect(g, lx, top + 1, 1, 4, P.treeDecidDark, 0.6)
  }
}
